"use client";

import { buildEventPath } from "@/lib/routes";
import { LuShieldCheck } from "react-icons/lu";

import type { PublicEvent } from "./components/EventTypes";

import { HeroSection } from "./components/HeroSection";
import { AboutSection } from "./components/AboutSection";
import { LineupSection } from "./components/LineupSection";
import { RulesSection } from "./components/RulesSection";
import { EventSidebar } from "./components/EventSidebar";
import { EventFooter } from "./components/EventFooter";
import { BottomActionBar } from "./components/BottomActionBar";

import { useEventPage } from "./hooks/useEventPage";
import { useEventHandlers } from "./hooks/useEventHandlers";

import { EventRestrictedGate } from "./EventRestrictedGate";
import { EventTopNav } from "./EventTopNav";
import { EventModals } from "./EventModals";
import { EventStagesCard } from "./EventStagesCard";

export default function GuestEventPage({
  initialEvent,
}: {
  initialEvent?: PublicEvent | null;
}) {
  const state = useEventPage(initialEvent);
  const handlers = useEventHandlers(state);

  const {
    event, isLoading, error, errorMessage,
    router, eventId, inviteId,
    isSignedIn,
    rsvpState, rsvpStatus, rsvpError, rsvpStatusLoaded,
    backgroundRef,
    saved, saving, handleShare, handleWhatsAppShare, handleAddToCalendar,
    isRestricted, eventStillRestricted,
    // Hero
    heroImages, heroSlide, setHeroSlide, setGalleryIndex, setGalleryOpen,
    moodTag, dateLabel, timeLabel, isOnlineEvent, isEnded, isCancelled,
    sortedPerformers,
    // About
    descExpanded, setDescExpanded, tags,
    // Stages
    stages, hasMultiStage,
    // Lineup
    expandedPerformerId, setExpandedPerformerId, showAllPerformers, setShowAllPerformers,
    // Rules
    ruleItems,
    // Sidebar
    priceLabel, startingFromPriceLabel, hasTicketTiers, ticketOptions,
    isApprovalEvent, needsRequest, isPromoted, urgencyTier, capacityTotal, spotsLeft,
    capacityLeftLabel, capacityTotalLabel, percentSold, hostLabel,
    hostStats, ratingLabel, showPaymentMethods,
    mapImageUrl, mapUrl,
    superGuest, invitesLeft, inviteInput, setInviteInput, inviteStatus, inviteError, canInvite,
    setReportOpen, setReportStatus, setReportError,
    // Bottom bar
    isPaidEvent, isFreeEvent, canRsvp, primaryCtaLabel, isModalOpen,
    failedPaymentInfo, retryingVerification,
    setTicketPickerOpen, setCancelConfirmOpen,
    // Modal state
    galleryOpen, mediaItems, galleryIndex,
    ticketPickerOpen, setCheckoutStep, setPendingOrder, setPromoValidation,
    coverChargeCategory, selectedTickets, setSelectedTickets,
    promoCode, setPromoCode, promoValidation, promoValidating,
    checkoutStep, formResponses, setFormResponses, pendingOrder, canContinueCheckout,
    dummyPaymentOpen, setDummyPaymentOpen, dummyPaymentOrder, setDummyPaymentOrder,
    dummyPaymentError, setDummyPaymentError, dummyPaymentLoading,
    setRsvpState,
    ticketModalOpen, setTicketModalOpen, ticketQrUrl, ticketCode, ticketRsvpId,
    purchasedTickets, setPurchasedTickets,
    cancelConfirmOpen, cashOnlyOpen, setCashOnlyOpen,
    reportOpen, reportReason, setReportReason, reportNotes, setReportNotes,
    reportEmail, setReportEmail, reportError, reportStatus,
    attendeesOpen, setAttendeesOpen, attendees, attendeesLoading,
    showAuthModal, setShowAuthModal, authModalMessage, setAuthModalMessage,
    showProfileGate, setShowProfileGate,
    signIn, setActive, signOut, getToken, user, dbEmail, savedSocialLinks,
    showInAppBrowserWarning, setShowInAppBrowserWarning, copiedLink, setCopiedLink,
    showBuyerForm, setShowBuyerForm, formFields, setFormFields,
    eventKey, pendingTicketForForm, setPendingTicketForForm,
  } = state;

  /* ------------------------------------------------------------------ */
  /*  Loading / Error states                                             */
  /* ------------------------------------------------------------------ */

  const inlineSkeleton = (
    <main className="relative min-h-screen bg-[#0b0c0f] text-white">
      {/* Hero skeleton */}
      <div className="relative h-[50vh] min-h-[360px] lg:h-[72vh] lg:min-h-[500px] lg:max-h-[720px] w-full bg-white/[0.03] animate-pulse" />
      {/* Content skeleton */}
      <div className="mx-auto max-w-[1200px] px-7 pt-6 pb-2">
        <div className="flex gap-2 mb-4">
          <div className="h-7 w-28 rounded-full bg-white/[0.06] animate-pulse" />
          <div className="h-7 w-16 rounded-full bg-white/[0.06] animate-pulse" />
        </div>
        <div className="h-12 w-3/4 rounded-lg bg-white/[0.06] animate-pulse mb-4" />
        <div className="flex gap-3 mb-8">
          <div className="h-5 w-32 rounded bg-white/[0.04] animate-pulse" />
          <div className="h-5 w-24 rounded bg-white/[0.04] animate-pulse" />
          <div className="h-5 w-40 rounded bg-white/[0.04] animate-pulse" />
        </div>
      </div>
      <div className="mx-auto max-w-[1200px] px-7">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_380px]">
          <div className="flex flex-col gap-6">
            <div className="h-40 rounded-[20px] bg-white/[0.03] animate-pulse" />
            <div className="h-32 rounded-[20px] bg-white/[0.03] animate-pulse" />
          </div>
          <div className="flex flex-col gap-4">
            <div className="h-48 rounded-[20px] bg-white/[0.03] animate-pulse" />
            <div className="h-32 rounded-[20px] bg-white/[0.03] animate-pulse" />
          </div>
        </div>
      </div>
    </main>
  );

  if (isLoading) return inlineSkeleton;

  if (error) {
    return (
      <div className="min-h-screen text-[var(--hopp-text)]">
        <div className="px-6 pb-24 pt-32 lg:px-10">
          <div className="rounded-2xl p-6 text-sm text-red-200 bg-[rgba(28,28,32,0.72)]">{errorMessage}</div>
        </div>
      </div>
    );
  }

  if (!event) return inlineSkeleton;

  /* ------------------------------------------------------------------ */
  /*  Restricted Event Gate                                              */
  /* ------------------------------------------------------------------ */

  if (isRestricted) {
    return (
      <EventRestrictedGate
        event={event}
        eventId={eventId}
        inviteId={inviteId}
        isSignedIn={isSignedIn}
        rsvpState={rsvpState}
        backgroundRef={backgroundRef}
        onGoBack={() => router.push("/")}
        onRsvp={() => { void handlers.handleRsvp(); }}
        onSignIn={() => {
          const target = eventId ? `${buildEventPath(eventId, "event")}${inviteId ? `?invite=${inviteId}` : ""}` : "/";
          router.push(`/auth?redirect=${encodeURIComponent(target)}`);
        }}
      />
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Main event page                                                    */
  /* ------------------------------------------------------------------ */

  return (
    <main className="relative min-h-screen bg-[#0b0c0f] text-white animate-fadeIn">
      <div ref={backgroundRef} className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[#0b0c0f]" />
      </div>
      <div className="relative z-10 min-h-screen w-full">
        <EventTopNav
          saved={saved}
          saving={saving}
          onGoBack={() => router.push("/")}
          onAddToCalendar={handleAddToCalendar}
          onShare={handleShare}
          onWhatsAppShare={handleWhatsAppShare}
          onToggleSave={handlers.handleSaveToggle}
        />

        {event ? (
          <>
            <HeroSection
              eventTitle={event.title}
              heroImages={heroImages}
              heroSlide={heroSlide}
              setHeroSlide={setHeroSlide}
              setGalleryIndex={setGalleryIndex}
              setGalleryOpen={setGalleryOpen}
              moodTag={moodTag}
              dateLabel={dateLabel}
              timeLabel={timeLabel}
              isOnlineEvent={isOnlineEvent}
              isEnded={isEnded}
              isCancelled={isCancelled}
              venue={event.venue}
              visibility={event.visibility}
              category={event.category}
              accessControl={event.accessControl}
              eventStartDate={event.date}
              eventStartTime={event.time}
              headliner={sortedPerformers.length > 0 ? { name: sortedPerformers[0].name, imageUrl: sortedPerformers[0].imageUrl, role: sortedPerformers[0].role } : null}
            />

            {/* PAGE BODY */}
            <div className="mx-auto max-w-[1200px] px-7 pt-10 pb-44">
              {rsvpState === "approved" && (
                <div className="mb-8 flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                  <LuShieldCheck size={20} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium text-white">You&apos;ve been approved</p>
                    <p className="mt-0.5 text-sm text-white/70">Select a ticket below to complete your registration.</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_380px]">
                {/* MAIN COLUMN */}
                <div className="flex flex-col gap-8 min-w-0">
                  <AboutSection
                    description={event.description}
                    descExpanded={descExpanded}
                    setDescExpanded={setDescExpanded}
                    tags={tags}
                  />

                  <EventStagesCard
                    stages={stages}
                    sortedPerformers={sortedPerformers}
                  />

                  <LineupSection
                    sortedPerformers={sortedPerformers}
                    stages={stages}
                    expandedPerformerId={expandedPerformerId}
                    setExpandedPerformerId={setExpandedPerformerId}
                    showAllPerformers={showAllPerformers}
                    setShowAllPerformers={setShowAllPerformers}
                  />

                  <RulesSection
                    ruleItems={ruleItems}
                    rulesAndRegulations={event?.rules?.rulesAndRegulations}
                  />
                </div>

                {/* SIDEBAR */}
                <EventSidebar
                  dateLabel={dateLabel}
                  timeLabel={timeLabel}
                  isOnlineEvent={isOnlineEvent}
                  venue={event.venue}
                  location={event.location}
                  priceLabel={priceLabel}
                  startingFromPriceLabel={startingFromPriceLabel}
                  hasTicketTiers={hasTicketTiers}
                  ticketTiersCount={ticketOptions.length}
                  rsvpState={rsvpState}
                  isEnded={isEnded}
                  isCancelled={isCancelled}
                  needsRequest={!!needsRequest}
                  requiresHostApproval={isApprovalEvent}
                  isPromoted={isPromoted}
                  urgencyTier={urgencyTier}
                  capacityTotal={capacityTotal}
                  spotsLeft={spotsLeft}
                  capacityLeftLabel={capacityLeftLabel}
                  capacityTotalLabel={capacityTotalLabel}
                  percentSold={percentSold}
                  hostLabel={hostLabel}
                  hostImageUrl={event.hostImageUrl ?? null}
                  ratingLabel={ratingLabel}
                  hostStats={hostStats}
                  showPaymentMethods={showPaymentMethods}
                  acceptedPaymentMethods={event.acceptedPaymentMethods ?? []}
                  mapImageUrl={mapImageUrl}
                  mapUrl={mapUrl}
                  locationPlaceName={event.locationPlaceName}
                  locationDetails={event.locationDetails}
                  showAddress={event.showAddress}
                  onlineUrl={event.onlineUrl}
                  superGuest={superGuest}
                  invitesLeft={invitesLeft}
                  inviteInput={inviteInput}
                  setInviteInput={setInviteInput}
                  inviteStatus={inviteStatus}
                  inviteError={inviteError}
                  canInvite={canInvite}
                  handleSendInvites={handlers.handleSendInvites}
                  onReportOpen={() => { setReportOpen(true); setReportStatus("idle"); setReportError(null); }}
                />
              </div>
            </div>

            <EventFooter />

            {/* Always render bottom bar */}
            {(!isSignedIn || !eventStillRestricted || rsvpStatusLoaded) && (
              <BottomActionBar
                event={event}
                rsvpState={rsvpState}
                rsvpStatus={rsvpStatus}
                rsvpError={rsvpError}
                isPaidEvent={isPaidEvent}
                isFreeEvent={isFreeEvent}
                isEnded={isEnded}
                isCancelled={isCancelled}
                isRestricted={isRestricted}
                hasTicketTiers={hasTicketTiers}
                needsRequest={!!needsRequest}
                canRsvp={canRsvp}
                startingFromPriceLabel={startingFromPriceLabel}
                priceLabel={priceLabel}
                primaryCtaLabel={primaryCtaLabel}
                isModalOpen={isModalOpen}
                failedPaymentInfo={failedPaymentInfo}
                retryingVerification={retryingVerification}
                onRsvp={handlers.handleRsvp}
                onOpenTicketPicker={() => setTicketPickerOpen(true)}
                onCancelConfirm={() => setCancelConfirmOpen(true)}
                onRetryVerification={handlers.handleRetryVerification}
              />
            )}
          </>
        ) : null}
      </div>

      {/* MODALS */}
      <EventModals
        galleryOpen={galleryOpen}
        setGalleryOpen={setGalleryOpen}
        mediaItems={mediaItems}
        galleryIndex={galleryIndex}
        setGalleryIndex={setGalleryIndex}
        ticketPickerOpen={ticketPickerOpen}
        setTicketPickerOpen={setTicketPickerOpen}
        setCheckoutStep={setCheckoutStep}
        setPendingOrder={setPendingOrder}
        setPromoValidation={setPromoValidation}
        ticketOptions={ticketOptions}
        coverChargeCategory={coverChargeCategory}
        selectedTickets={selectedTickets}
        setSelectedTickets={setSelectedTickets}
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        promoValidation={promoValidation}
        promoValidating={promoValidating}
        checkoutStep={checkoutStep}
        formResponses={formResponses}
        setFormResponses={setFormResponses}
        pendingOrder={pendingOrder}
        maxTicketsPerOrder={event?.maxTicketsPerOrder ?? 8}
        ticketCategories={event?.ticketCategories}
        rsvpState={rsvpState}
        rsvpError={rsvpError}
        canContinueCheckout={canContinueCheckout}
        onApplyPromo={handlers.handleApplyPromo}
        onTicketCheckout={handlers.handleTicketCheckout}
        onConfirmPayment={handlers.handleConfirmPayment}
        dummyPaymentOpen={dummyPaymentOpen}
        setDummyPaymentOpen={setDummyPaymentOpen}
        dummyPaymentOrder={dummyPaymentOrder}
        setDummyPaymentOrder={setDummyPaymentOrder}
        dummyPaymentError={dummyPaymentError}
        setDummyPaymentError={setDummyPaymentError}
        dummyPaymentLoading={dummyPaymentLoading}
        setRsvpState={setRsvpState}
        onDummyPaymentSubmit={handlers.handleDummyPaymentSubmit}
        ticketModalOpen={ticketModalOpen}
        setTicketModalOpen={setTicketModalOpen}
        ticketQrUrl={ticketQrUrl}
        ticketCode={ticketCode}
        ticketRsvpId={ticketRsvpId}
        purchasedTickets={purchasedTickets}
        setPurchasedTickets={setPurchasedTickets}
        cancelConfirmOpen={cancelConfirmOpen}
        setCancelConfirmOpen={setCancelConfirmOpen}
        isPaidEvent={isPaidEvent}
        onCancelRsvp={handlers.handleCancelRsvp}
        cashOnlyOpen={cashOnlyOpen}
        setCashOnlyOpen={setCashOnlyOpen}
        onCashOnlyConfirm={handlers.handleCashOnlyConfirm}
        reportOpen={reportOpen}
        setReportOpen={setReportOpen}
        reportReason={reportReason}
        setReportReason={setReportReason}
        reportNotes={reportNotes}
        setReportNotes={setReportNotes}
        reportEmail={reportEmail}
        setReportEmail={setReportEmail}
        reportError={reportError}
        reportStatus={reportStatus}
        isSignedIn={isSignedIn}
        onSubmitReport={handlers.handleSubmitReport}
        attendeesOpen={attendeesOpen}
        setAttendeesOpen={setAttendeesOpen}
        attendees={attendees}
        attendeesLoading={attendeesLoading}
        showAuthModal={showAuthModal}
        setShowAuthModal={setShowAuthModal}
        authModalMessage={authModalMessage}
        setAuthModalMessage={setAuthModalMessage}
        eventId={eventId}
        inviteId={inviteId}
        router={router}
        showInAppBrowserWarning={showInAppBrowserWarning}
        setShowInAppBrowserWarning={setShowInAppBrowserWarning}
        copiedLink={copiedLink}
        setCopiedLink={setCopiedLink}
        showBuyerForm={showBuyerForm}
        setShowBuyerForm={setShowBuyerForm}
        formFields={formFields}
        setFormFields={setFormFields}
        eventKey={eventKey}
        pendingTicketForForm={pendingTicketForForm}
        setPendingTicketForForm={setPendingTicketForForm}
        onShowTicketQr={handlers.showTicketQr}
        showProfileGate={showProfileGate}
        setShowProfileGate={setShowProfileGate}
        onProfileGateComplete={handlers.handleProfileGateComplete}
        onProfileGateClose={handlers.handleProfileGateClose}
        onContinueAsGuest={handlers.handleContinueAsGuest}
        dbEmail={dbEmail}
        savedSocialLinks={savedSocialLinks}
        event={event}
        user={user}
        getToken={getToken}
        signIn={signIn}
        setActive={setActive}
        signOut={signOut}
      />
    </main>
  );
}
